import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../api';
import { Classification } from './ClassificationBadge';
import { DocumentItem } from './DocumentItemsPanel';

interface Customer {
  customer_name: string;
  customer_ico: string | null;
}

interface ChartAccount {
  code: string;
  name: string;
  classification?: string;
  note?: string;
}

interface Props {
  item: DocumentItem;
  vendor?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const ClassificationOverrideModal: React.FC<Props> = ({ item, vendor, onClose, onSaved }) => {
  const [classification, setClassification] = useState<Classification>(item.classification || 'unclear');
  const [clientIco, setClientIco] = useState<string>(item.classified_client_ico || '');
  const [accountCode, setAccountCode] = useState<string>(item.account_code || '');
  const [isPayment, setIsPayment] = useState<boolean>(!!item.is_payment);
  const [customers, setCustomers] = useState<Customer[]>([]);
  // Fulltext výber klienta — zoznam má desiatky položiek, natívny <select>
  // je na mobile nepoužiteľný. Hľadá sa v názve aj IČO, diakritika sa ignoruje.
  const [clientQuery, setClientQuery] = useState('');
  const [clientHighlight, setClientHighlight] = useState(0);
  const norm = (v: string) => (v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filteredCustomers = React.useMemo(() => {
    const q = norm(clientQuery.trim());
    if (!q) return [];
    return customers
      .filter(c => norm(c.customer_name).includes(q) || (c.customer_ico || '').includes(q))
      .slice(0, 30);
  }, [customers, clientQuery]);
  const [selectedName, setSelectedName] = useState<string>(item.classified_client_name || '');
  const selectedCustomer: Customer | null = clientIco
    ? (customers.find(c => c.customer_ico === clientIco) || { customer_name: selectedName || clientIco, customer_ico: clientIco })
    : (selectedName ? (customers.find(c => !c.customer_ico && c.customer_name === selectedName) || { customer_name: selectedName, customer_ico: null }) : null);
  // iOS PWA: výber klienta nahradí pole, ktoré má otvorenú klávesnicu → iOS ju
  // zavrie, ale stránku nechá posunutú a ťuky/rolovanie v okne idú vedľa
  // („Uložiť“ nereagovalo, 19. a 20. 10.). Pole preto najprv zavrieme sami a po
  // zavretí klávesnice vrátime stránku tam, kde bola pri otvorení okna.
  const searchRef = useRef<HTMLInputElement>(null);
  const scrollAtOpen = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const restoreScroll = () => setTimeout(() => window.scrollTo(0, scrollAtOpen.current), 60);
  const pickCustomer = (c: Customer) => { searchRef.current?.blur(); setClientIco(c.customer_ico || ''); setSelectedName(c.customer_name); setClientQuery(''); restoreScroll(); };
  // Okno drží viditeľnú časť obrazovky (visualViewport) — pri otvorenej klávesnici
  // ostane celé dosiahnuteľné, vrátane tlačidiel dole.
  const readViewport = () => {
    const v = typeof window !== 'undefined' ? window.visualViewport : null;
    return v ? { top: v.offsetTop, height: v.height } : null;
  };
  const [viewport, setViewport] = useState(readViewport);
  useEffect(() => {
    const v = window.visualViewport;
    if (!v) return;
    const on = () => setViewport({ top: v.offsetTop, height: v.height });
    v.addEventListener('resize', on);
    v.addEventListener('scroll', on);
    return () => { v.removeEventListener('resize', on); v.removeEventListener('scroll', on); };
  }, []);
  const [chartAccounts, setChartAccounts] = useState<ChartAccount[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [createRule, setCreateRule] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/classification/customers')
      .then(res => { if (!cancelled) setCustomers(res.data || []); })
      .catch(() => { if (!cancelled) setCustomers([]); })
      .finally(() => { if (!cancelled) setLoadingCustomers(false); });
    api.get('/classification/chart')
      .then(res => { if (!cancelled) setChartAccounts(res.data?.costs || []); })
      .catch(() => { if (!cancelled) setChartAccounts([]); });
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    // Konkrétny klient je VOLITEĽNÝ aj pri klasifikácii "Klient" — pre zdieľané
    // resale náklady (napr. MDM delené na viacerých klientov) sa nechá prázdny
    // a rozdelenie sa rieši vo FLOWii pri tvorbe výstupných faktúr.
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/classification/items/${item.id}`, {
        classification,
        client_ico: classification === 'client' ? clientIco : null,
        client_name: classification === 'client' ? (selectedCustomer?.customer_name || null) : null,
        create_rule: createRule,
        account_code: accountCode.trim() || null,
        is_payment: isPayment,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Chyba pri ukladaní');
      setSaving(false);
    }
  };

  // Portál do <body>: okno sa otvára z detailu dokladu, ktorý na mobile roluje
  // (.doc-modal-body, -webkit-overflow-scrolling). iOS pri position:fixed vnútri
  // rolovacej oblasti po posune vyhodnocuje dotyky inde, než okno vidno —
  // „Uložiť“ nereagovalo (PWA, nahrávky 19. a 20. 10. 2026).
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', left: 0, right: 0,
        top: viewport ? viewport.top : 0, height: viewport ? viewport.height : '100%',
        background: 'rgba(0,0,0,0.5)', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        onBlur={e => {
          // fokus ide do iného poľa → klávesnica ostáva, stránku nevraciame
          const next = e.relatedTarget as HTMLElement | null;
          if (!next || !/^(INPUT|TEXTAREA|SELECT)$/.test(next.tagName)) restoreScroll();
        }}
        style={{
          background: '#fff', borderRadius: 8, padding: 24,
          maxWidth: 520, width: '100%', maxHeight: '100%', overflowY: 'auto',
          boxSizing: 'border-box', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
          boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
        }}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>Manuálna klasifikácia</h3>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16, lineHeight: 1.5 }}>
          <div><b>{item.description}</b></div>
          <div>{vendor ? `od ${vendor} · ` : ''}{item.total_price ? `${item.total_price} €` : ''}</div>
        </div>

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: 12, background: isPayment ? '#dbeafe' : '#f9fafb',
          border: isPayment ? '1px solid #93c5fd' : '1px solid transparent',
          borderRadius: 6, marginBottom: 16, cursor: 'pointer', fontSize: 13,
        }}>
          <input
            type="checkbox"
            checked={isPayment}
            onChange={e => setIsPayment(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            <b>Toto je platba / vyúčtovanie preddavku.</b>
            <div style={{ color: '#374151', marginTop: 2 }}>
              Záporný riadok ktorý znižuje sumu na úhradu (napr. „Platobná karta",
              „Zaplatený preddavok"). V Omege sa zaúčtuje ako MD 321 / DAL 314,
              nie ako nákladová položka.
            </div>
          </span>
        </label>

        <div style={{ marginBottom: 16, opacity: isPayment ? 0.4 : 1, pointerEvents: isPayment ? 'none' : 'auto' }}>
          <label style={labelStyle}>Klasifikácia</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(['client', 'internal', 'unclear'] as const).map(c => (
              <label key={c} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px',
                border: `2px solid ${classification === c ? '#2563eb' : '#e5e7eb'}`,
                borderRadius: 6, cursor: 'pointer',
                background: classification === c ? '#eff6ff' : '#fff',
                fontSize: 13,
              }}>
                <input
                  type="radio"
                  checked={classification === c}
                  onChange={() => setClassification(c)}
                  style={{ margin: 0 }}
                />
                {c === 'client' ? 'Klient' : c === 'internal' ? 'Interné' : 'Nejasné'}
              </label>
            ))}
          </div>
        </div>

        {classification === 'client' && (
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Konkrétny klient (voliteľné)</label>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
              Vyber klienta ak ide o preúčtovanie jednému klientovi. Pri zdieľaných
              nákladoch (napr. MDM delené na viacerých) nechaj prázdne — rozdelenie
              spravíš pri vystavení faktúry v module Fakturácia.
            </div>
            {loadingCustomers ? (
              <div style={{ fontSize: 13, color: '#6b7280' }}>Načítavam klientov…</div>
            ) : customers.length === 0 ? (
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Žiadni klienti zatiaľ — nechaj prázdne. Partneri sa dopĺňajú sami z faktúr, alebo ich načítaš v module Partneri cez „Import z histórie".
              </div>
            ) : selectedCustomer ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: 6, fontSize: 14 }}>
                <span style={{ flex: 1 }}>
                  <b>{selectedCustomer.customer_name}</b>
                  {selectedCustomer.customer_ico ? <span style={{ color: '#6b7280' }}> · IČO {selectedCustomer.customer_ico}</span> : null}
                </span>
                <button type="button" onClick={() => { setClientIco(''); setSelectedName(''); setClientQuery(''); }}
                  style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 18, lineHeight: 1 }} title="Zrušiť výber">×</button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <input
                  ref={searchRef}
                  value={clientQuery}
                  onChange={e => { setClientQuery(e.target.value); setClientHighlight(0); }}
                  onKeyDown={e => {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setClientHighlight(i => Math.min(i + 1, filteredCustomers.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setClientHighlight(i => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter') { e.preventDefault(); const c = filteredCustomers[clientHighlight]; if (c) pickCustomer(c); }
                  }}
                  placeholder="Hľadaj klienta (názov alebo IČO)…"
                  autoComplete="off"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                />
                {clientQuery.trim().length > 0 && (
                  <div style={{ border: '1px solid #d1d5db', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: 220, overflowY: 'auto', background: '#fff' }}>
                    {filteredCustomers.length === 0 ? (
                      <div style={{ padding: '8px 10px', fontSize: 13, color: '#6b7280' }}>
                        Nič sa nenašlo. Nového klienta pridáš v module Partneri.
                      </div>
                    ) : filteredCustomers.map((c, i) => (
                      <div key={c.customer_ico || c.customer_name}
                        onMouseDown={e => { e.preventDefault(); pickCustomer(c); }}
                        onMouseEnter={() => setClientHighlight(i)}
                        style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13.5, background: i === clientHighlight ? '#eff6ff' : 'transparent', borderBottom: '1px solid #f3f4f6' }}>
                        {c.customer_name}{c.customer_ico ? <span style={{ color: '#9ca3af' }}> · IČO {c.customer_ico}</span> : null}
                      </div>
                    ))}
                  </div>
                )}
                {clientQuery.trim().length === 0 && (
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                    {customers.length} klientov · začni písať názov alebo IČO
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: 16, opacity: isPayment ? 0.4 : 1, pointerEvents: isPayment ? 'none' : 'auto' }}>
          <label style={labelStyle}>Účtovný účet (voliteľné)</label>
          {chartAccounts.length > 0 ? (
            <select
              value={accountCode}
              onChange={e => setAccountCode(e.target.value)}
              style={{
                width: '100%', padding: '8px 10px',
                border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
                marginBottom: 6,
              }}
            >
              <option value="">— nevyplnené —</option>
              {chartAccounts.map(a => (
                <option key={a.code} value={a.code}>
                  {a.code} — {a.name}{a.note ? ` · ${a.note}` : ''}
                </option>
              ))}
            </select>
          ) : null}
          <input
            type="text"
            value={accountCode}
            onChange={e => setAccountCode(e.target.value)}
            placeholder="napr. 518.000 (vlastný kód mimo rozvrhu)"
            style={{
              width: '100%', padding: '8px 10px',
              border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14,
              boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
            Tento účet sa použije pri Omega exporte (MD strana účtovného zápisu).
          </div>
        </div>

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: 12, background: '#f9fafb', borderRadius: 6,
          marginBottom: 16, cursor: 'pointer', fontSize: 13,
        }}>
          <input
            type="checkbox"
            checked={createRule}
            onChange={e => setCreateRule(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            <b>Vytvoriť pravidlo pre podobné položky.</b>
            <div style={{ color: '#6b7280', marginTop: 2 }}>
              Budúce položky od dodávateľa „{vendor || '?'}" s podobným popisom dostanú automaticky
              rovnakú klasifikáciu aj účet. (Systém sa okrem pravidiel učí aj z histórie — ručne
              zaúčtovaná položka sa pri ďalšej podobnej použije ako vzor aj bez pravidla.)
            </div>
          </span>
        </label>

        {error && (
          <div style={{
            padding: 10, marginBottom: 12,
            background: '#fef2f2', color: '#b91c1c',
            borderRadius: 6, fontSize: 13,
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '10px 18px', border: '1px solid #d1d5db',
              borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 14,
            }}
          >Zrušiť</button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '10px 18px', border: 'none',
              borderRadius: 6, background: '#2563eb', color: '#fff',
              cursor: saving ? 'wait' : 'pointer', fontSize: 14, fontWeight: 500,
              opacity: saving ? 0.6 : 1,
            }}
          >{saving ? 'Ukladám…' : 'Uložiť'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#374151',
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  marginBottom: 8,
};

export default ClassificationOverrideModal;
